import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { 
  PlusIcon, 
  CloudArrowUpIcon,
  CloudArrowDownIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  PhoneIcon,
  UserIcon,
  EnvelopeIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  CheckIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

import { leadService } from '../../services/leadService';
import { campaignService } from '../../services/campaignService';
import { Lead, LeadStatus, LeadFilters, isLeadScrubImmediateResult } from '../../types/lead';
import { Campaign } from '../../types/campaign';
import { formatLeadCarrier, formatLeadLineType, mergeLeadLookupFields, normalizeLeadLookupFields } from '../../utils/leadLookupDisplay';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { SkeletonTable } from '../../components/UI/SkeletonLoader';
import Breadcrumb from '../../components/UI/Breadcrumb';
import { Table, Pagination, Column } from '../../components/UI/Table';
import StatusBadge from '../../components/UI/StatusBadge';
import Button from '../../components/UI/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/UI/Card';
import ConfirmationDialog from '../../components/UI/ConfirmationDialog';
import BulkActionModal from '../../components/UI/BulkActionModal';
import PhoneLookupModal from '../../components/Leads/PhoneLookupModal';
import LeadRowActionsMenu from '../../components/Leads/LeadRowActionsMenu';
import { useAuth } from '../../contexts/AuthContext';

const LeadsPage: React.FC = () => {
  const { id: campaignId } = useParams<{ id: string }>();
  const { user, isAdmin } = useAuth();
  const isUser = user?.role === 'user' || user?.role === 'agent';
  const [leads, setLeads] = useState<Lead[]>([]);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<LeadFilters>({
    status: undefined,
    search: '',
    page: 1,
    limit: 50
  });
  const [totalCount, setTotalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState<Lead | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [availableStatuses, setAvailableStatuses] = useState<LeadStatus[]>([]);
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [showBulkActionConfirm, setShowBulkActionConfirm] = useState(false);
  const [bulkAction, setBulkAction] = useState<{
    action: 'update_status' | 'delete' | 'do_not_call' | 'remove_do_not_call';
    data?: { status?: LeadStatus; notes?: string };
  } | null>(null);
  const [isBulkActioning, setIsBulkActioning] = useState(false);
  const [showBulkActionModal, setShowBulkActionModal] = useState(false);
  const [bulkActionType, setBulkActionType] = useState<'selected' | 'all' | null>(null);
  const [pendingBulkStatus, setPendingBulkStatus] = useState<LeadStatus | null>(null);
  const [exporting, setExporting] = useState(false);
  const [expandedAltNumbers, setExpandedAltNumbers] = useState<Set<string>>(new Set());
  const [showPhoneLookup, setShowPhoneLookup] = useState(false);
  const [showDncScrub, setShowDncScrub] = useState(false);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookingUpLeadId, setLookingUpLeadId] = useState<string | null>(null);
  const canRunPhoneLookup = isAdmin || user?.role === 'user';

  const fetchCampaign = useCallback(async () => {
    if (!campaignId) return;
    
    try {
      const campaignData = await campaignService.getCampaign(campaignId);
      setCampaign(campaignData);
    } catch (error) {
      console.error('Failed to fetch campaign:', error);
    }
  }, [campaignId]);

  const fetchAvailableStatuses = useCallback(async () => {
    if (!campaignId) return;
    
    try {
      // Use the dedicated API endpoint to fetch unique statuses
      const statuses = await leadService.getLeadStatuses(campaignId);
      setAvailableStatuses(statuses.sort() as LeadStatus[]);
    } catch (error) {
      console.error('Failed to fetch available statuses:', error);
      setAvailableStatuses([]);
    }
  }, [campaignId]);

  const fetchLeads = useCallback(async (opts?: { silent?: boolean }) => {
    if (!campaignId) return;
    
    try {
      if (!opts?.silent) setLoading(true);
      const response = await leadService.getLeads(campaignId, filters);
      const leadsData = Array.isArray(response.data) ? response.data.map(normalizeLeadLookupFields) : [];
      setLeads(leadsData);
      setTotalCount(response.pagination?.total || leadsData.length);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      if (!opts?.silent) {
        setLeads([]);
        setTotalCount(0);
      }
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [campaignId, filters.status, filters.timezone, filters.search, filters.page, filters.limit]);

  useEffect(() => {
    fetchCampaign();
    fetchAvailableStatuses();
    fetchLeads();
  }, [fetchCampaign, fetchAvailableStatuses, fetchLeads]);

  const handleSearch = (search: string) => {
    setFilters(prev => ({ ...prev, search, page: 1 }));
  };

  const handleStatusFilter = (status: LeadStatus | undefined) => {
    setFilters(prev => ({ ...prev, status: status ? [status] : undefined, page: 1 }));
  };

  const handleTimezoneFilter = (
    timezone: 'Eastern' | 'Central' | 'Mountain' | 'Pacific' | 'Unknown' | 'all'
  ) => {
    setFilters(prev => ({ ...prev, timezone, page: 1 }));
  };

  const handleDeleteClick = (lead: Lead) => {
    setLeadToDelete(lead);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!leadToDelete) return;

    try {
      setIsDeleting(true);
      await leadService.deleteLead(leadToDelete.id);
      
      // Refresh the leads list
      await fetchLeads();
      
      // Close the dialog
      setShowDeleteConfirm(false);
      setLeadToDelete(null);
      
      // TODO: Show success toast
    } catch (error) {
      console.error('Failed to delete lead:', error);
      // TODO: Show error toast
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setLeadToDelete(null);
  };

  const handleSelectLead = (leadId: string) => {
    setSelectedLeads(prev => {
      if (prev.includes(leadId)) {
        return prev.filter(id => id !== leadId);
      } else {
        return [...prev, leadId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map(lead => lead.id));
    }
  };

  const handleBulkAction = (action: 'update_status' | 'delete' | 'do_not_call' | 'remove_do_not_call', data?: { status?: LeadStatus; notes?: string }) => {
    setBulkAction({ action, data });
    setShowBulkActionConfirm(true);
  };

  const handleBulkActionConfirm = async () => {
    if (!bulkAction) return;

    try {
      setIsBulkActioning(true);
      let result: { success: number; failed: number } = { success: 0, failed: 0 };
      
      if (bulkActionType === 'all') {
        result = await leadService.bulkActionByQuery({
          action: bulkAction.action,
          campaignId: campaignId!,
          filters,
          data: bulkAction.data
        });
      } else {
        if (selectedLeads.length === 0) return;
        result = await leadService.bulkAction({
          action: bulkAction.action,
          leadIds: selectedLeads,
          data: bulkAction.data
        });
      }
      
      await fetchLeads();
      
      setSelectedLeads([]);
      setShowBulkActionConfirm(false);
      setBulkAction(null);
      setBulkActionType(null);
      setPendingBulkStatus(null);

      if (bulkAction.action === 'delete') {
        const failedNote = result.failed > 0 ? `, ${result.failed} failed` : '';
        toast.success(`Deleted ${result.success} ${result.success === 1 ? 'lead' : 'leads'}${failedNote}`);
      } else {
        toast.success('Bulk action completed');
      }
    } catch (error: any) {
      console.error('Failed to perform bulk action:', error);
      const serverMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message;
      toast.error(serverMessage || 'Failed to perform bulk action. Please try again.');
    } finally {
      setIsBulkActioning(false);
    }
  };

  const handleBulkActionCancel = () => {
    setShowBulkActionConfirm(false);
    setBulkAction(null);
  };

  const handleOpenBulkActionModal = () => {
    if (selectedLeads.length === 0) return;
    setShowBulkActionModal(true);
  };

  const handleBulkDeleteClick = () => {
    if (selectedLeads.length === 0) return;
    setBulkActionType('selected');
    handleBulkAction('delete');
  };

  const toggleAltNumbers = (leadId: string) => {
    setExpandedAltNumbers((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) {
        next.delete(leadId);
      } else {
        next.add(leadId);
      }
      return next;
    });
  };

  const handleCloseBulkActionModal = () => {
    setShowBulkActionModal(false);
    setBulkActionType(null);
    setPendingBulkStatus(null);
  };

  const handleApplyToSelected = (status: LeadStatus) => {
    setBulkActionType('selected');
    setPendingBulkStatus(status);
    setShowBulkActionModal(false);
    setShowBulkActionConfirm(true);
    setBulkAction({ action: 'update_status', data: { status } });
  };

  const handleApplyToAll = (status: LeadStatus) => {
    setBulkActionType('all');
    setPendingBulkStatus(status);
    setShowBulkActionModal(false);
    setShowBulkActionConfirm(true);
    setBulkAction({ action: 'update_status', data: { status } });
  };

  const runPhoneLookupForIds = async (leadIds: string[]) => {
    if (!campaignId || leadIds.length === 0 || lookupBusy) return;
    setLookupBusy(true);
    if (leadIds.length === 1) setLookingUpLeadId(leadIds[0]!);
    try {
      const result = await leadService.startPhoneLookup(campaignId, { leadIds });
      const patches = isLeadScrubImmediateResult(result) ? result.leads : undefined;
      if (patches?.length) {
        setLeads((prev) => mergeLeadLookupFields(prev, patches));
      }
      if (isLeadScrubImmediateResult(result)) {
        const first = result.leads?.[0];
        if (result.leads?.length === 1 && first && (first.lineType || first.carrierName || first.carrier)) {
          toast.success(
            `Lookup saved: ${formatLeadCarrier(first.carrierName, first.carrier)} · ${formatLeadLineType(first.lineType)}`
          );
        } else {
          toast.success(
            result.updatedLeads > 0
              ? `Phone lookup updated ${result.updatedLeads.toLocaleString()} lead${result.updatedLeads === 1 ? '' : 's'}${result.skippedLeads ? `, skipped ${result.skippedLeads}` : ''}.`
              : result.skippedLeads > 0
                ? 'Phone lookup skipped — already looked up recently.'
                : 'No matching leads to look up.'
          );
        }
      } else {
        toast.success(`Phone lookup queued for ${result.totalLeads.toLocaleString()} leads`);
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string }; message?: string } } };
      toast.error(err.response?.data?.error?.message || err.response?.data?.message || 'Phone lookup failed');
    } finally {
      setLookupBusy(false);
      setLookingUpLeadId(null);
    }
  };

  const handlePhoneLookupClick = () => {
    if (selectedLeads.length > 0) {
      void runPhoneLookupForIds(selectedLeads);
      return;
    }
    setShowPhoneLookup(true);
  };

  const handleDncClick = () => {
    if (selectedLeads.length > 0) {
      void (async () => {
        if (!campaignId || lookupBusy) return;
        setLookupBusy(true);
        try {
          const result = await leadService.startDncScrub(campaignId, { leadIds: selectedLeads });
          if (isLeadScrubImmediateResult(result)) {
            toast.success(
              `DNC scrub finished. Updated ${result.updatedLeads.toLocaleString()}, skipped ${result.skippedLeads.toLocaleString()}.`
            );
          } else {
            toast.success(`DNC scrub queued for ${result.totalLeads.toLocaleString()} leads`);
          }
          await fetchLeads();
        } catch (error: unknown) {
          const err = error as { response?: { data?: { error?: { message?: string }; message?: string } } };
          toast.error(err.response?.data?.error?.message || err.response?.data?.message || 'DNC scrub failed');
        } finally {
          setLookupBusy(false);
        }
      })();
      return;
    }
    setShowDncScrub(true);
  };

  // Define table columns
  const columns: Column[] = [
    {
      key: 'select',
      header: (
        <div className="flex items-center justify-center">
          <input
            type="checkbox"
            className="checkbox"
            checked={leads.length > 0 && selectedLeads.length === leads.length}
            onChange={handleSelectAll}
          />
        </div>
      ),
      render: (lead) => (
        <div className="flex items-center justify-center">
          <input
            type="checkbox"
            className="checkbox"
            checked={selectedLeads.includes(lead.id)}
            onChange={() => handleSelectLead(lead.id)}
          />
        </div>
      ),
      width: '50px',
    },
    {
      key: 'lead',
      header: 'Lead',
      render: (lead) => (
        <div>
          <div className="font-medium text-foreground">{lead.fullName}</div>
          {lead.externalRef && (
            <div className="text-sm text-muted-foreground">#{lead.externalRef}</div>
          )}
        </div>
      ),
    },
    {
      key: 'contact',
      header: 'Contact',
      render: (lead) => {
        const altNumbers = (lead.phoneNumbers || []).filter((pn: any) => pn.phoneNumber !== lead.phoneE164);
        const isExpanded = expandedAltNumbers.has(lead.id);
        const visibleAltNumbers = isExpanded ? altNumbers : altNumbers.slice(0, 2);
        const hiddenCount = Math.max(altNumbers.length - 2, 0);

        return (
          <div>
            <div className="text-sm text-foreground">{lead.phoneE164}</div>
            {altNumbers.length > 0 && (
              <div className="mt-1 space-y-0.5">
                {visibleAltNumbers.map((pn: any) => (
                  <div key={pn.id} className="text-xs text-muted-foreground">
                    Alt: {pn.phoneNumber}
                    {!pn.isActive ? ' (archived)' : ''}
                  </div>
                ))}
                {altNumbers.length > 2 && (
                  <button
                    type="button"
                    onClick={() => toggleAltNumbers(lead.id)}
                    className="text-xs text-primary hover:underline"
                  >
                    {isExpanded ? 'Show less' : `Show more (${hiddenCount})`}
                  </button>
                )}
              </div>
            )}
            {lead.email && (
              <div className="text-sm text-muted-foreground">{lead.email}</div>
            )}
          </div>
        );
      },
    },
    {
      key: 'company',
      header: 'Company',
      render: (lead) => (
        <div className="text-sm text-foreground">
          {lead.companyName || <span className="text-muted-foreground">-</span>}
        </div>
      ),
    },
    {
      key: 'line_type',
      header: 'Type',
      render: (lead) => (
        <div className="text-sm text-foreground">{formatLeadLineType(lead.lineType)}</div>
      ),
    },
    {
      key: 'carrier',
      header: 'Carrier',
      render: (lead) => (
        <div className="text-sm text-foreground">
          {formatLeadCarrier(lead.carrierName, lead.carrier)}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (lead) => (
        <StatusBadge status={lead.status} type="lead" variant="default" />
      ),
    },
    ...(campaign?.campaignType === 'text'
      ? [
          {
            key: 'sms_status',
            header: 'SMS Status',
            render: (lead: Lead) => {
              const deliveryStatus = lead.smsLastOutboundStatus;
              if (!deliveryStatus && !(lead.smsSentCount || 0)) {
                return <span className="text-sm text-muted-foreground">Not sent</span>;
              }
              if (!deliveryStatus) {
                return <span className="text-sm text-muted-foreground">—</span>;
              }
              const colors: Record<string, string> = {
                delivered: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
                sent: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
                queued: 'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-400',
                failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                undelivered: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
              };
              return (
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                    colors[deliveryStatus] || 'bg-muted text-muted-foreground'
                  }`}
                >
                  {deliveryStatus}
                </span>
              );
            },
          },
          {
            key: 'sms_sent',
            header: 'Sent / Replied',
            render: (lead: Lead) => (
              <span className="text-sm text-foreground">
                {lead.smsSentCount || 0}
                <span className="text-muted-foreground mx-1">/</span>
                <span className={(lead.smsRepliedCount || 0) > 0 ? 'text-green-600 dark:text-green-400' : ''}>
                  {lead.smsRepliedCount || 0}
                </span>
              </span>
            ),
          },
          {
            key: 'last_sms',
            header: 'Last SMS',
            render: (lead: Lead) => {
              if (!lead.lastSmsAt) {
                return <span className="text-sm text-muted-foreground">—</span>;
              }
              return (
                <div className="text-sm text-foreground">
                  <div>Sent {new Date(lead.lastSmsAt).toLocaleString()}</div>
                  {lead.smsDeliveredAt && (
                    <div className="text-xs text-green-600 dark:text-green-400">
                      Delivered {new Date(lead.smsDeliveredAt).toLocaleString()}
                    </div>
                  )}
                </div>
              );
            },
          },
        ]
      : [
          {
            key: 'last_call',
            header: 'Last Call',
            render: (lead: Lead) =>
              lead.lastCallAt ? (
                <span className="text-sm text-foreground">
                  {new Date(lead.lastCallAt).toLocaleDateString()}
                </span>
              ) : (
                <span className="text-sm text-muted-foreground">Never</span>
              ),
          },
          {
            key: 'attempts',
            header: 'Attempts',
            render: (lead: Lead) => (
              <span className="text-sm text-foreground">{lead.callAttemptsCount || 0}</span>
            ),
          },
        ]),
    {
      key: 'actions',
      header: '',
      width: '52px',
      render: (lead) =>
        campaignId ? (
          <LeadRowActionsMenu
            lead={lead}
            campaignId={campaignId}
            canRunPhoneLookup={canRunPhoneLookup}
            isAdmin={isAdmin}
            lookupBusy={lookupBusy}
            lookingUp={lookingUpLeadId === lead.id}
            onLookup={() => void runPhoneLookupForIds([lead.id])}
            onDelete={() => handleDeleteClick(lead)}
          />
        ) : null,
      align: 'right' as const,
    },
  ];

  const handleExportLeads = async () => {
    try {
      if (totalCount === 0) {
        toast.error('No leads to export for current filters');
        return;
      }
      setExporting(true);
      await leadService.exportLeads(campaignId!, {
        format: 'csv',
        fields: ['id', 'externalRef', 'fullName', 'phoneE164', 'email', 'companyName', 'status', 'lastCallAt', 'createdAt'],
        filters: {
          status: filters.status,
          search: filters.search,
          hasEmail: filters.hasEmail,
          createdBefore: filters.createdBefore,
          createdAfter: filters.createdAfter,
          lastCallBefore: filters.lastCallBefore,
          lastCallAfter: filters.lastCallAfter,
        }
      });
      toast.success('Export started. Your download should begin shortly.');
    } catch (error) {
      console.error('Failed to export leads:', error);
      toast.error('Failed to export leads. Please try again.');
    } finally {
      setExporting(false);
    }
  };



  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: 'Campaigns', href: '/campaigns' },
          { label: campaign?.name || 'Campaign', href: `/campaigns/${campaignId}` },
          { label: 'Leads', current: true }
        ]}
      />
      
      {/* Header */}
      <Card className="mb-8">
        <CardContent className="p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-2 text-foreground">Campaign Leads</h1>
              <p className="text-muted-foreground">
                Manage leads for this campaign ({totalCount} total)
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {(isAdmin || isUser) && (
                <>
                  <Button variant="outlined" size="sm" onClick={handleExportLeads} disabled={exporting}>
                    <CloudArrowDownIcon className="-ml-0.5 mr-2 h-4 w-4" />
                    Export Leads
                  </Button>
                  <Button variant="secondary" size="sm" to={`/campaigns/${campaignId}/leads/import`}>
                    <CloudArrowUpIcon className="-ml-0.5 mr-2 h-4 w-4" />
                    Import CSV
                  </Button>
                  <Button variant="primary" size="sm" to={`/campaigns/${campaignId}/leads/new`}>
                    <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
                    Add Lead
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedLeads.length > 0 && (isAdmin || canRunPhoneLookup) && (
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CheckIcon className="h-5 w-5 text-primary mr-2" />
                <span className="text-sm font-medium">
                  {selectedLeads.length} {selectedLeads.length === 1 ? 'lead' : 'leads'} selected
                </span>
              </div>
              <div className="flex items-center space-x-3">
                {canRunPhoneLookup && (
                  <Button
                    variant="outlined"
                    size="sm"
                    onClick={() => void runPhoneLookupForIds(selectedLeads)}
                    disabled={lookupBusy}
                    loading={lookupBusy}
                  >
                    <MagnifyingGlassIcon className="-ml-0.5 mr-2 h-4 w-4" />
                    Phone Lookup
                  </Button>
                )}
                {isAdmin && (
                  <>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDeleteClick}
                  disabled={selectedLeads.length === 0 || isBulkActioning}
                >
                  <TrashIcon className="-ml-0.5 mr-2 h-4 w-4" aria-hidden="true" />
                  Delete
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleOpenBulkActionModal}
                  disabled={selectedLeads.length === 0}
                >
                  Bulk Actions
                </Button>
                  </>
                )}
                <Button
                  variant="outlined"
                  size="sm"
                  onClick={() => setSelectedLeads([])}
                >
                  Clear Selection
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters and Search */}
      <Card>
        <CardContent className="px-4 py-5 sm:p-6">
          <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <div className="flex-1 min-w-[12rem] max-w-lg">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-muted-foreground" />
                </div>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="input pl-10"
                  placeholder="Search leads by name, phone, or email..."
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={filters.timezone || 'all'}
                onChange={(e) => handleTimezoneFilter(e.target.value as 'Eastern' | 'Central' | 'Mountain' | 'Pacific' | 'Unknown' | 'all')}
                className="select w-auto min-w-[9.5rem]"
                aria-label="Timezone"
              >
                <option value="all">All timezones</option>
                <option value="Eastern">Eastern</option>
                <option value="Central">Central</option>
                <option value="Mountain">Mountain</option>
                <option value="Pacific">Pacific</option>
                <option value="Unknown">Unknown</option>
              </select>
              {canRunPhoneLookup && (
                <>
                  <Button variant="outlined" size="sm" onClick={handleDncClick} disabled={lookupBusy}>
                    <NoSymbolIcon className="-ml-0.5 mr-2 h-4 w-4" />
                    DNC
                  </Button>
                  <Button variant="outlined" size="sm" onClick={handlePhoneLookupClick} disabled={lookupBusy}>
                    <MagnifyingGlassIcon className="-ml-0.5 mr-2 h-4 w-4" />
                    Phone Lookup
                  </Button>
                </>
              )}
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant="outlined"
                size="sm"
              >
                <FunnelIcon className="-ml-0.5 mr-2 h-4 w-4" />
                Filters
              </Button>
            </div>
          </div>

          {showFilters && (
            <div className="border-t border-border pt-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Status
                  </label>
                  <select
                    value={filters.status || ''}
                    onChange={(e) => handleStatusFilter(e.target.value as LeadStatus || undefined)}
                    className="select"
                  >
                    <option value="">All Statuses</option>
                    {availableStatuses.map(status => {
                      // Format status for display
                      const displayName = status
                        .split('_')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ');
                      
                      return (
                        <option key={status} value={status}>
                          {displayName}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Timezone
                  </label>
                  <select
                    value={filters.timezone || 'all'}
                    onChange={(e) => handleTimezoneFilter(e.target.value as 'Eastern' | 'Central' | 'Mountain' | 'Pacific' | 'Unknown' | 'all')}
                    className="select"
                  >
                    <option value="all">All Timezones</option>
                    <option value="Eastern">Eastern</option>
                    <option value="Central">Central</option>
                    <option value="Mountain">Mountain</option>
                    <option value="Pacific">Pacific</option>
                    <option value="Unknown">Unknown</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card className="overflow-hidden">
        {loading ? (
          <SkeletonTable rows={8} columns={6} />
        ) : leads.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto h-16 w-16 rounded-full bg-muted/50 flex items-center justify-center">
              <UserIcon className="h-8 w-8 text-primary" />
            </div>
            <h3 className="mt-4 text-sm font-semibold text-foreground">No leads found</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {filters.search || filters.status || (filters.timezone && filters.timezone !== 'all')
                ? 'No leads match your current filters.'
                : 'Get started by adding your first lead or importing a CSV file.'
              }
            </p>
            {!filters.search && !filters.status && (!filters.timezone || filters.timezone === 'all') && (isAdmin || isUser) && (
              <div className="mt-6">
                <Button variant="primary" size="sm" to={`/campaigns/${campaignId}/leads/new`}>
                  <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
                  Add Lead
                </Button>
              </div>
            )}
          </div>
        ) : (
          <Table
            data={leads}
            columns={columns}
            loading={loading}
          />
        )}
      </Card>

      {/* Pagination */}
      {totalCount > filters.limit! && (
        <Pagination
          currentPage={filters.page!}
          totalPages={Math.ceil(totalCount / filters.limit!)}
          onPageChange={(page) => setFilters(prev => ({ ...prev, page }))}
          totalItems={totalCount}
          itemsPerPage={filters.limit!}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Lead"
        message={`Are you sure you want to delete "${leadToDelete?.fullName}"? This action cannot be undone and will also delete all associated call attempts.`}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
        variant="destructive"
      />

      {campaignId && (
        <>
          <PhoneLookupModal
            isOpen={showPhoneLookup}
            onClose={() => setShowPhoneLookup(false)}
            campaignId={campaignId}
            availableStatuses={availableStatuses}
            kind="phone_lookup"
            onComplete={() => void fetchLeads({ silent: true })}
          />
          <PhoneLookupModal
            isOpen={showDncScrub}
            onClose={() => setShowDncScrub(false)}
            campaignId={campaignId}
            availableStatuses={availableStatuses}
            kind="dnc"
            onComplete={() => void fetchLeads({ silent: true })}
          />
        </>
      )}

      {/* Bulk Action Modal */}
      <BulkActionModal
        isOpen={showBulkActionModal}
        onClose={handleCloseBulkActionModal}
        onApplyToSelected={handleApplyToSelected}
        onApplyToAll={handleApplyToAll}
        selectedCount={selectedLeads.length}
        totalCount={totalCount}
        availableStatuses={['new', 'interested', 'not_interested']}
        isLoading={isBulkActioning}
      />

      {/* Bulk Action Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showBulkActionConfirm}
        onClose={handleBulkActionCancel}
        onConfirm={handleBulkActionConfirm}
        title={bulkAction?.action === 'delete' ? 'Delete Selected Leads' : 'Confirm Bulk Status Update'}
        message={
          bulkAction?.action === 'delete'
            ? `Are you sure you want to delete ${selectedLeads.length} selected ${selectedLeads.length === 1 ? 'lead' : 'leads'}? This action cannot be undone and will also delete all associated call attempts.`
            : bulkActionType === 'all'
              ? `Are you sure you want to update the status of all ${totalCount} leads matching current filters to ${pendingBulkStatus?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}?`
              : `Are you sure you want to update the status of ${selectedLeads.length} selected leads to ${pendingBulkStatus?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}?`
        }
        confirmText={bulkAction?.action === 'delete' ? 'Delete' : 'Update Status'}
        cancelText="Cancel"
        isLoading={isBulkActioning}
        variant={bulkAction?.action === 'delete' ? 'destructive' : 'primary'}
      />
    </div>
  );
};

export default LeadsPage;